import * as React from "react";
import { IStaticMultiSelectDropdownProps } from "../../Interfaces/dropdownInterfaces";
import { Select } from "antd";
import { CloseCircleOutlined } from "@ant-design/icons";
import { matchesDropdownSearch } from "src/Utils/dropdownSearch";

const { Option } = Select;

const toSelectValue = (value: unknown) => value?.toString() ?? "";

export const StaticMultiSelectDropdown: React.FC<
  IStaticMultiSelectDropdownProps
> = ({
  value,
  onChange,
  items,
  label,
  name,
  className,
  extraLabel,
  disabled = false,
  withSelectAll = false,
  withTagBox = false,
  allDefaultValue,
  hideDropdown,
  lockedValues = [],
}) => {
  const [searchValue, setSearchValue] = React.useState("");
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;

  const isLocked = (id: number | string) =>
    lockedValues.some(
      (lockedValue) => toSelectValue(lockedValue) === toSelectValue(id),
    );

  const addLockedValues = (selectedValues: Array<number | string>) => {
    const lockedItemIds = items
      .filter((item) => isLocked(item.id))
      .map((item) => item.id);

    return [
      ...lockedItemIds,
      ...selectedValues.filter(
        (selectedValue) =>
          !lockedItemIds.some(
            (lockedItemId) =>
              toSelectValue(lockedItemId) === toSelectValue(selectedValue),
          ),
      ),
    ];
  };

  React.useEffect(() => {
    if (!allDefaultValue || !items || !items.length) return;
    onChangeRef.current(
      name,
      items.map((i) => i.id),
    );
  }, [allDefaultValue, items, name]);

  const handleSearch = (value: string) => {
    setSearchValue(value);
  };

  const clearSearch = () => {
    setSearchValue("");
  };

  const handleChange = (value: string[] | null | undefined) => {
    if (value && value.length && value.includes("all")) {
      const all = items.map((i) => i.id);
      if (value.length === all.length + 1) {
        onChange(name, addLockedValues([]));
      } else onChange(name, [...all]);
    } else {
      if (!value) {
        onChange(name, addLockedValues([]));
        return;
      }
      const v = value.map((x) => {
        const item = items.find((i) => toSelectValue(i.id) === x);
        return item?.id ?? x;
      });

      onChange(name, addLockedValues(v));
      setSearchValue("");
    }
  };

  function getOptions() {
    return items.map((option) => ({
      value: toSelectValue(option.id),
      label: option.name,
      additional: option.additional,
      prefix: option.prefix,
    }));
  }

  const getValue = () => {
    let res: string[] = [];
    if (value && value.length) res = value.map(toSelectValue);
    return res;
  };

  const deleteTagHandler = (id: number | string) => {
    if (!value || isLocked(id)) return;

    onChange(
      name,
      value.filter((v) => toSelectValue(v) !== toSelectValue(id)),
    );
  };

  if (!items) return <></>;

  return (
    <div className="w-full flex flex-col justify-start items-start gap-2">
      {extraLabel ? <div className="text-[10px]">{label}</div> : <></>}
      {!hideDropdown ? (
        <div className="relative w-full">
          <Select
            disabled={disabled}
            value={getValue()}
            className={className}
            mode="multiple"
            showSearch
            allowClear
            searchValue={searchValue}
            onSearch={handleSearch}
            onBlur={clearSearch}
            onDropdownVisibleChange={(open) => {
              if (!open) clearSearch();
            }}
            placeholder={label}
            style={{ width: "100%" }}
            dropdownMatchSelectWidth={false}
            popupClassName="dropdown-popup-fit-content"
            getPopupContainer={() => document.body}
            onChange={handleChange}
            filterOption={false}
            onClear={clearSearch}
            tagRender={() => <></>}
          >
            {withSelectAll ? (
              <Option key="all" value="all">
                انتخاب همه
              </Option>
            ) : null}
            {getOptions()
              .filter((option) =>
                matchesDropdownSearch(option.label, searchValue),
              )
              .map((option) => (
                <Option
                  key={option.value}
                  value={option.value}
                  disabled={isLocked(option.value)}
                >
                  <div className="inline-flex flex-row justify-start items-center gap-2 whitespace-nowrap">
                    {option.additional ? <div>{option.additional}</div> : null}
                    <span>{option.label}</span>
                    {option.prefix ? <div>{option.prefix}</div> : null}
                  </div>
                </Option>
              ))}
          </Select>
          {value?.length && !searchValue ? (
            <div className="absolute top-1.5 right-2 text-xs pointer-events-none select-none">
              {value.length} مورد انتخاب شده است
            </div>
          ) : null}
        </div>
      ) : (
        <></>
      )}
      {withTagBox && value && value.length && items && items.length ? (
        <div className="px-2 py-1 flex flex-row flex-wrap justify-start items-center gap-3 border-gray-300 rounded border-2 w-full max-h-20 overflow-y-auto">
          {getValue().map((v) => {
            const item = items.find((i) => i.id?.toString() === v?.toString());
            if (!item) return <></>;
            return (
              <div
                key={v}
                className="p-1 flex flex-row justify-center items-center gap-2 text-xs bg-gray-100 border-[1px] border-gray-300 rounded"
              >
                <div>
                  {item.name}
                  {isLocked(v) ? " (اجباری)" : ""}
                </div>
                {!disabled && !isLocked(v) ? (
                  <div
                    className="text-sky-700 cursor-pointer pb-px"
                    onClick={() => deleteTagHandler(v)}
                  >
                    <CloseCircleOutlined />
                  </div>
                ) : (
                  <></>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};
